import React, { useState, useEffect, createContext, useContext, useMemo, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { runMigrations, ensureSystemTables } from '../lib/migrations'
import { canFor, ROLE_CAPABILITIES, effectiveCapabilities } from './capabilities'
import { setThemePref } from '../lib/theme'

export const ADMIN_EMAIL = 'jaylen@beatybrothersmoving.com'

// SUPER_ADMIN_EMAILS is retained only for the auto-promote path at
// first-login profile creation (so if Julian/Jensen/Greg ever lose their
// row and sign in fresh, they re-land on the correct role). The runtime
// authorization check is role-based, not email-based — see useCan().
export const SUPER_ADMIN_EMAILS = [
  'jaylen@beatybrothersmoving.com',
  'julian@beatybrothersmoving.com',
  'jensen@beatybrothersmoving.com',
  'gdbeaty@gmail.com',
]

export const AuthContext = createContext()

// How long the initial `getSession()` call is allowed to hang before we
// give up and surface a connection-trouble screen. Supabase's auth API
// can take a few seconds in the cold-start case, but anything over ~15s
// almost certainly means the gateway is broken (the May 19 incident
// kept the loading screen hanging for 20+ hours).
const AUTH_INIT_TIMEOUT_MS = 15000

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [userRole, setUserRole] = useState(null)
  const [permissionOverrides, setPermissionOverrides] = useState([])
  // The person's name as the office knows it (user_profiles.display_name),
  // for "who did this" stamps such as the move date-confirmation log
  // (2026-09-18). Falls back to the email's local part.
  const [displayName, setDisplayName] = useState(null)
  const [needsPassword, setNeedsPassword] = useState(false)
  const [authLoading, setAuthLoading] = useState(true)
  // Non-null when the initial auth bootstrap fails or times out. The
  // AuthGate (App.jsx) checks this and shows a ConnectionTrouble screen
  // with a manual retry button. `null` means "no error" — the standard
  // happy path.
  const [authError, setAuthError] = useState(null)
  // Increments to re-run the auth-init effect. Bumping this is how the
  // retry button on the error screen tells us to try again.
  const [authRetryNonce, setAuthRetryNonce] = useState(0)

  // Load or create user profile with role + permission overrides
  const loadUserProfile = async (authUser) => {
    if (!authUser) { setUserRole(null); setPermissionOverrides([]); return }
    try {
      // Check if profile exists
      let { data: profile, error } = await supabase.from('user_profiles').select('*').eq('user_id', authUser.id).single()
      if (error && error.code === 'PGRST116') {
        // Crew portal accounts (crew-auth-plan-v1) have NO profile row BY
        // DESIGN — is_staff() must stay false for them. If one lands on
        // the office app (shared browser session), bounce to their portal
        // instead of minting an office profile. The RLS self-insert
        // policy blocks crew-linked accounts anyway (sql/053); this just
        // makes the UX intentional instead of a console error.
        if (authUser.user_metadata?.crew_portal) {
          window.location.replace('/crew')
          return
        }
        // No profile yet — create one
        // Check if there's an invite with a specific role
        const userEmail = (authUser.email || '').toLowerCase()
        let assignedRole = 'office'
        if (userEmail === ADMIN_EMAIL) {
          assignedRole = 'owner'
        } else if (SUPER_ADMIN_EMAILS.includes(userEmail)) {
          assignedRole = 'super_admin'
        } else {
          try {
            const { data: invite } = await supabase.from('user_invites').select('role').eq('email', userEmail).single()
            if (invite?.role) assignedRole = invite.role
          } catch(e) {} // invite table might not exist yet
        }
        const { data: newProfile, error: insertErr } = await supabase.from('user_profiles').insert({
          user_id: authUser.id,
          email: authUser.email || '',
          display_name: (authUser.email || '').split('@')[0],
          role: assignedRole,
          password_set: false,
        }).select().single()
        if (insertErr) { console.error('Failed to create user profile:', insertErr); setUserRole('office'); setPermissionOverrides([]); return }
        profile = newProfile
        // New invite user — needs to set password
        if (userEmail !== ADMIN_EMAIL) setNeedsPassword(true)
      } else if (error) {
        // Table might not exist yet — default to office, or owner for the admin email
        console.warn('user_profiles query failed (table may not exist yet):', error.message)
        setUserRole((authUser.email || '').toLowerCase() === ADMIN_EMAIL ? 'owner' : 'office')
        setPermissionOverrides([])
        return
      }
      setUserRole(profile.role || 'office')
      setPermissionOverrides(Array.isArray(profile.permission_overrides) ? profile.permission_overrides : [])
      setDisplayName(profile.display_name || (authUser.email || '').split('@')[0] || null)
      // Per-user theme (sql/091 theme_pref): the profile wins over the
      // device-local copy so Jaylen's phone and Lisa's Mac each follow
      // their own person. Missing column / null → leave the device as is.
      if (profile.theme_pref) setThemePref(profile.theme_pref)
      // Cache the resolved role locally (2026-08-26 offline pass): when
      // the profile fetch fails with no signal, the catch below restores
      // this instead of silently degrading an estimator to 'office'.
      try {
        localStorage.setItem(`bbm_profile_${authUser.id}`, JSON.stringify({
          role: profile.role || 'office',
          overrides: Array.isArray(profile.permission_overrides) ? profile.permission_overrides : [],
        }))
      } catch (_) { /* private mode etc. — non-fatal */ }
      // Check if password still needs to be set
      if (profile.password_set === false && (authUser.email || '').toLowerCase() !== ADMIN_EMAIL) {
        setNeedsPassword(true)
      } else {
        setNeedsPassword(false)
      }
      // Mark invite as accepted if one exists
      if (authUser.email) {
        try { await supabase.from('user_invites').update({ status: 'accepted', accepted_at: new Date().toISOString() }).eq('email', authUser.email.toLowerCase()).eq('status', 'pending') } catch(e) {}
      }
    } catch (e) {
      console.error('Error loading user profile:', e)
      // Offline fallback (2026-08-26): use the locally cached role from
      // the last successful load — a no-signal walkthrough must not
      // demote Jensen/Julian to 'office' and lock the estimator UI.
      try {
        const cached = JSON.parse(localStorage.getItem(`bbm_profile_${authUser.id}`) || 'null')
        if (cached?.role) {
          console.warn('Profile fetch failed — using cached role:', cached.role)
          setUserRole(cached.role)
          setPermissionOverrides(Array.isArray(cached.overrides) ? cached.overrides : [])
          return
        }
      } catch (_) { /* fall through to the defaults */ }
      setUserRole((authUser.email || '').toLowerCase() === ADMIN_EMAIL ? 'owner' : 'office')
      setPermissionOverrides([])
    }
  }

  useEffect(() => {
    // Reset error/loading state on each (re-)attempt. The retry button on
    // the connection-trouble screen bumps authRetryNonce, which re-runs
    // this whole effect.
    setAuthError(null)
    setAuthLoading(true)
    let cancelled = false
    let timeoutId

    // Race getSession() against a hard timeout. Before May 19 the
    // call could hang indefinitely (Supabase gateway 522'ing meant
    // the fetch never resolved), which left the LoadingScreen up for
    // 20+ hours. Now if Supabase isn't responsive within
    // AUTH_INIT_TIMEOUT_MS, we surface an error instead of hanging.
    const timeoutPromise = new Promise((_, reject) => {
      timeoutId = setTimeout(
        () => reject(new Error('Connection timeout — Supabase did not respond in time')),
        AUTH_INIT_TIMEOUT_MS
      )
    })

    Promise.race([supabase.auth.getSession(), timeoutPromise])
      .then(async ({ data: { session } }) => {
        if (cancelled) return
        clearTimeout(timeoutId)
        const authUser = session?.user || null
        setUser(authUser)
        await loadUserProfile(authUser)
        if (!cancelled) setAuthLoading(false)
      })
      .catch((err) => {
        if (cancelled) return
        clearTimeout(timeoutId)
        console.error('Auth bootstrap failed:', err)
        setAuthError(err?.message || 'Failed to reach the server')
        setAuthLoading(false)
      })

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const authUser = session?.user || null
      setUser(authUser)
      loadUserProfile(authUser)
    })
    return () => {
      cancelled = true
      clearTimeout(timeoutId)
      subscription.unsubscribe()
    }
  }, [authRetryNonce])

  // Re-run the auth bootstrap effect. Wired into the AuthGate's retry
  // button on the connection-trouble screen.
  const retryAuth = useCallback(() => {
    setAuthRetryNonce(n => n + 1)
  }, [])

  const signIn = async (email, password) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) throw error
  }

  const signUp = async (email, password) => {
    const { error } = await supabase.auth.signUp({ email, password })
    if (error) throw error
  }

  const signOut = async () => {
    await supabase.auth.signOut()
    setUser(null)
    setUserRole(null)
    setPermissionOverrides([])
  }

  // Role-derived flags. Kept around for the small number of places where a
  // role check is genuinely the right primitive (e.g. "show Owner badge").
  // For feature/UI gating use `can(...)` from useCan instead.
  const isOwner      = userRole === 'owner'
  const isSuperAdmin = userRole === 'super_admin' || userRole === 'owner'
  const isAdmin      = userRole === 'admin' || userRole === 'super_admin' || userRole === 'owner'

  // The capability predicate. Stable across renders unless role or overrides
  // change. Components call `const can = useCan(); can('view_financials')`.
  const can = useMemo(() => {
    const fn = (cap) => canFor(userRole, permissionOverrides, cap)
    fn.role = userRole
    fn.overrides = permissionOverrides
    return fn
  }, [userRole, permissionOverrides])

  const completePasswordSetup = async (password) => {
    const { error } = await supabase.auth.updateUser({ password })
    if (error) throw error
    // Mark password as set in profile
    await supabase.from('user_profiles').update({ password_set: true }).eq('user_id', user.id)
    setNeedsPassword(false)
  }

  // Theme preference: apply now (device-local, instant) and persist to the
  // profile. The column may not exist until sql/091 is applied — a failed
  // save is logged, not surfaced; the local copy still works.
  const saveThemePref = useCallback(async (pref) => {
    setThemePref(pref)
    if (!user) return
    try {
      const { error } = await supabase.from('user_profiles').update({ theme_pref: pref }).eq('user_id', user.id)
      if (error) console.warn('theme_pref not saved (apply sql/091?):', error.message)
    } catch (e) { console.warn('theme_pref not saved:', e?.message) }
  }, [user])

  return (
    <AuthContext.Provider
      value={{
        user,
        displayName: displayName || (user?.email || '').split('@')[0] || null,
        saveThemePref,
        userRole,
        permissionOverrides,
        // Legacy boolean flags — kept for backward compatibility during the
        // sweep that replaces these with can('...') checks. Prefer useCan().
        isAdmin,
        isOwner,
        isSuperAdmin,
        // Capability predicate — the new canonical authorization primitive.
        can,
        needsPassword,
        authLoading,
        // Connection-trouble surface — non-null when the initial auth
        // bootstrap timed out or failed. AuthGate shows ErrorScreen when
        // this is set. Call retryAuth() to re-attempt.
        authError,
        retryAuth,
        signIn,
        signUp,
        signOut,
        completePasswordSetup,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() { return useContext(AuthContext) }

// useCan — convenience hook that returns the capability predicate directly.
// Usage:
//   const can = useCan()
//   if (can('view_financials')) { ... }
// This is the recommended way to gate UI from May 2026 onward.
export function useCan() {
  const ctx = useContext(AuthContext)
  return ctx?.can || (() => false)
}
